import React, { useEffect, useState } from "react";
import {
  Page,
  Card,
  Text,
  Box,
  Button,
  TextField,
  InlineStack,
  ProgressBar,
} from "@shopify/polaris";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import SetupTabs from "./SetupTabs";
import { useContent } from "../Provider/ContentContext";

function GenericContentInput({ shopOrigin, apiKey }) {
  const navigate = useNavigate();
  const { contentData, setContentData } = useContent();
  const [keywords, setKeywords] = useState("");
  const [step, setStep] = useState(2);
  const [progress, setProgress] = useState(0);
  const [progressVisible, setProgressVisible] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [mobileViewHeight, setMobileViewHeight] = useState(0);

  useEffect(() => {
      const updateHeight = () => {
        if (window.innerWidth <= 768) {
          setMobileViewHeight(window.innerHeight);
        }
      };

      updateHeight();
      window.addEventListener('resize', updateHeight);
      window.addEventListener('orientationchange', updateHeight);

      return () => {
        window.removeEventListener('resize', updateHeight);
        window.removeEventListener('orientationchange', updateHeight);
      };
    }, []);

    // Then apply dynamic styles to your tone-radio div
    const mobileStyles = window.innerWidth <= 768 ? {
      position: 'sticky',
      bottom: 0,
      left: 0,
      width: '100%',
      zIndex: 1000,
      backgroundColor: '#F1F1F1',
      // boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
      padding: '15px 12px',
      margin: 0
  } : {};

  function startProgressBar(onComplete) {
    setProgressVisible(true);
    let progressValue = 0;
    const animatedDuration = 30000; // 30 seconds
    const intervalTime = 50;
    const startTime = Date.now();

    const interval = setInterval(() => {
      const elapsedTime = Date.now() - startTime;
      progressValue = Math.min(98, (elapsedTime / animatedDuration) * 98);
      setProgress(progressValue);

      if (progressValue >= 98 || elapsedTime >= animatedDuration) {
        clearInterval(interval);
        setProgress(98);
      }
    }, intervalTime);

    // Return a function to complete the progress bar
    return () => {
      clearInterval(interval);
      setProgress(100);
      setTimeout(() => {
        setProgressVisible(false);
        onComplete && onComplete();
      }, 500); // Optional: hide after short delay
    };
  }

  const handleContinue = async () => {
    if (!keywords.trim()) return;

    setIsSubmitting(true);

    setContentData((prev) => ({
      ...prev,
      keywords: keywords,
      content_type: "generic_content",
    }));

    // Start progress bar and pass onComplete handler
    let completeProgressBar = startProgressBar(() => {
      setIsSubmitting(false);
    });

    try {
      // Save tone/content
      const payload = {
        shop: shopOrigin || "",
        keywords,
        content_type: "generic_content",
        tone: contentData.tone || "friendly",
        language: contentData.language || "English",
      };

      await axios.post("/api/save-tone-content/", payload, {
        headers: { "Content-Type": "application/json" },
      });

      await axios.post(
        "/api/store-content/",
        { ...contentData, ...payload },
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      // Regenerate titles (same as ArticleSuggestions)
      const response = await axios.get(
        `/api/regenerate-titles/${encodeURIComponent(shopOrigin || "")}/`,
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      if (completeProgressBar) completeProgressBar();

      if (response.data.status === "success" && response.data.title_teasers) {
        navigate(
          `/regenerate-title-combos?shop=${encodeURIComponent(
            shopOrigin || ""
          )}`,
          {
            state: { titleTeasers: response.data.title_teasers },
          }
        );
      } else {
        console.error("No title teasers found:", response.data.message);
        setIsSubmitting(false);
      }
    } catch (err) {
      console.error(
        "Error regenerating titles:",
        err.response?.data || err.message
      );
      if (completeProgressBar) completeProgressBar();
      setIsSubmitting(false);
    }
  };
  // const customStyles=`

  // `;

  return (
    <Page title="BlogHero Setup">
      {/* <style>{customStyles}</style> */}
      <SetupTabs step={step} setStep={setStep} />
      <Card padding={{ xs: "600", sm: "800" }} sectioned>
        <Box paddingBlockEnd="400">
          <Text variant="headingMd" as="h1">
            I want to create generic content
          </Text>
          {/* <Text variant="bodyMd" as="p" color="subdued">
            Let's understand what topics you want to write about
          </Text> */}
        </Box>

        <Box paddingBlockEnd="400">
          <TextField
            label="Type keywords related to the content you want to generate"
            value={keywords}
            onChange={setKeywords}
            placeholder="e.g. Ways to apply makeup, skincare routine, makeup trends"
            helpText="These keywords help us understand your point of focus. Keep them short and specific."
          />
        </Box>
        <Box>
          <div>
            {progressVisible && (
              <ProgressBar progress={progress} size="small" />
            )}
          </div>
        </Box>
      </Card>
      <div style={{ marginTop: "15px", marginBottom: "10px", marginLeft: "24px", marginRight:"24px", ...mobileStyles  }} className="tone-radio">
        <InlineStack align="space-between">
          <Button
            size="medium"
            disabled={isSubmitting}
            onClick={() =>
              navigate(
                `/content-setup?shop=${encodeURIComponent(shopOrigin || "")}`
              )
            }
          >
            Back
          </Button>
          <Button
            size="medium"
            variant="primary"
            disabled={!keywords.trim() || isSubmitting}
            onClick={handleContinue}
          >
            Generate Suggestions
          </Button>
        </InlineStack>
      </div>
    </Page>
  );
}

export default GenericContentInput;
